//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { useEffect, useRef } from 'react';
import { useAuthHelper } from '../../AuthHelper';

// Function used to re-render the component 2 minutes before session timeout
// to ensure that the OpenSearch Dashboards session is always active.
// The interval itself keeps running regardless of `visible` — only the
// callback is skipped while hidden. Recreating the interval on every
// visibility toggle (e.g. grid <-> detail view) would reset its countdown
// each time, so a user who switches views more often than reRenderInterval
// would never trigger the keepalive at all.
export function useDashboardRerenderInterval(
  setDashboardLoaded: (v: boolean) => void,
  visible: boolean = true,
) {
  const { INACTIVITY_TIMEOUT } = useAuthHelper();
  const INACTIVITY_BUFFER = 2 * 60 * 1000; // Used to re-render 2 minutes before expiration
  const reRenderInterval = INACTIVITY_TIMEOUT - INACTIVITY_BUFFER;

  const visibleRef = useRef(visible);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!visibleRef.current) return;
      setDashboardLoaded(false); // Trigger re-render
    }, reRenderInterval);
    return () => clearInterval(interval);
  }, [reRenderInterval, setDashboardLoaded]);
}
